import { motion } from 'framer-motion'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from 'recharts'
import {
  TrendingUp,
  Clock,
  Users,
  Code2,
  Smile,
  Brain,
  Zap,
  GitPullRequest,
  CheckCircle2,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'
import AnimatedCounter from '../components/Animations/AnimatedCounter'

interface SlideProps {
  isActive: boolean
}

// Research-backed metrics from GitHub, Accenture, ACM studies
const keyStats = [
  {
    icon: Clock,
    value: 55,
    suffix: '%',
    label: 'Faster Task Completion',
    color: 'text-electric-cyan',
    bgColor: 'from-electric-cyan/20 to-electric-cyan/5',
    source: 'GitHub Research',
  },
  {
    icon: Code2,
    value: 46,
    suffix: '%',
    label: 'Code Written by AI',
    color: 'text-electric-purple',
    bgColor: 'from-electric-purple/20 to-electric-purple/5',
    source: 'GitHub Copilot',
  },
  {
    icon: GitPullRequest,
    value: 4,
    suffix: 'x',
    label: 'Faster PR Cycles',
    color: 'text-aurora-teal',
    bgColor: 'from-aurora-teal/20 to-aurora-teal/5',
    source: 'GitHub (9.6→2.4 days)',
  },
  {
    icon: Users,
    value: 15,
    suffix: 'M+',
    label: 'Developers Using AI',
    color: 'text-electric-orange',
    bgColor: 'from-electric-orange/20 to-electric-orange/5',
    source: '400% YoY Growth',
  },
]

// Productivity improvements by metric
const productivityImpact = [
  { metric: 'Task Completion Speed', value: 88, color: '#00f5ff' },
  { metric: 'Repetitive Tasks', value: 96, color: '#8b5cf6' },
  { metric: 'Successful Builds', value: 84, color: '#14b8a6' },
  { metric: 'Stay in Flow State', value: 73, color: '#ff6b35' },
]

// Developer experience metrics from Accenture study
const developerExperience = [
  { icon: Smile, label: 'Feel More Fulfilled', value: 90, source: 'Accenture' },
  { icon: Zap, label: 'Enjoy Coding More', value: 91, source: 'Accenture' },
  { icon: Brain, label: 'Reduced Mental Effort', value: 70, source: 'Accenture' },
  { icon: CheckCircle2, label: 'Code Suggestions Kept', value: 88, source: 'GitHub' },
]

export default function ProductivityMetricsSlide({ isActive: _isActive }: SlideProps) {
  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-4 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
              Productivity <span className="text-gradient">Impact</span>
            </h2>
            <p className="text-base text-white/60">
              Research-backed metrics from GitHub, Accenture & ACM studies
            </p>
          </div>
        </StaggerItem>

        {/* Key Stats row */}
        <StaggerItem>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {keyStats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                  className={`glass rounded-xl p-3 bg-gradient-to-br ${stat.bgColor} transition-all duration-300`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${stat.color}`} />
                    <span className="text-xs text-white/60">{stat.label}</span>
                  </div>
                  <div className={`text-2xl md:text-3xl font-bold ${stat.color}`}>
                    <AnimatedCounter
                      end={stat.value}
                      suffix={stat.suffix}
                      duration={1.5}
                    />
                  </div>
                  <p className="text-[10px] text-white/40 mt-1">{stat.source}</p>
                </motion.div>
              )
            })}
          </div>
        </StaggerItem>

        {/* Main content grid */}
        <StaggerItem className="flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
            {/* Productivity Impact Bar Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="glass rounded-2xl p-4 flex flex-col"
            >
              <h3 className="text-lg font-semibold text-white mb-1">
                Developer Self-Reported Improvements
              </h3>
              <p className="text-xs text-white/40 mb-3">
                % of developers reporting improvement in each area
              </p>
              <div className="flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={productivityImpact} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                    <XAxis
                      type="number"
                      stroke="#ffffff40"
                      fontSize={11}
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <YAxis
                      dataKey="metric"
                      type="category"
                      stroke="#ffffff40"
                      fontSize={11}
                      width={130}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#12121a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        color: '#fff',
                      }}
                      labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                      itemStyle={{ color: '#00f5ff' }}
                      formatter={(value: number) => [`${value}% of developers`, 'Reported']}
                    />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                      {productivityImpact.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-white/30 mt-2">
                Source: GitHub Copilot Research 2024
              </p>
            </motion.div>

            {/* Developer Experience Panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="glass rounded-2xl p-4 flex flex-col"
            >
              <h3 className="text-lg font-semibold text-white mb-1">
                Developer Experience & Satisfaction
              </h3>
              <p className="text-xs text-white/40 mb-3">
                Impact on job satisfaction and workflow quality
              </p>
              <div className="flex-1 flex flex-col gap-3">
                {developerExperience.map((item, index) => {
                  const Icon = item.icon
                  return (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-electric-cyan" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white/80">{item.label}</span>
                          <span className="text-sm font-bold text-electric-cyan">{item.value}%</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${item.value}%` }}
                            transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                            className="h-full bg-gradient-to-r from-electric-cyan to-electric-purple rounded-full"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
              <p className="text-[10px] text-white/30 mt-3">
                Source: Accenture Developer Study 2024 • GitHub Research
              </p>
            </motion.div>
          </div>
        </StaggerItem>

        {/* Key insight + Sources */}
        <StaggerItem>
          <div className="mt-4 mb-[100px]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="glass rounded-xl p-3 max-w-3xl mx-auto mb-3"
            >
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-electric-purple flex-shrink-0" />
                <p className="text-sm text-white/70">
                  <span className="text-electric-cyan font-semibold">Key Finding:</span>{' '}
                  90% of engineering teams now use AI tools. It takes ~11 weeks for developers to fully realize productivity gains.
                </p>
              </div>
            </motion.div>
            <p className="text-xs text-white/40 text-center">
              Sources: GitHub Copilot Research (ACM 2024) • Accenture Developer Study • Jellyfish State of Engineering 2025
            </p>
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
